/**
 * Detailed Stripe setup guide — shown in a Dialog with a print-friendly
 * layout so the operator can save it as PDF via the browser's built-in
 * "Print → Save as PDF" flow. No PDF library needed.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BookOpen, Printer, ExternalLink } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export const StripeSetupGuide = () => {
  const doPrint = () => {
    // Print styles are scoped in App.css → @media print { .print-only, ... }
    // We add a body class briefly so only the guide prints.
    document.body.classList.add("print-only-guide");
    // Small delay so React renders the class before browser snapshot.
    setTimeout(() => {
      window.print();
      // Cleanup on next tick (dialog stays open after user closes print).
      setTimeout(() => document.body.classList.remove("print-only-guide"), 400);
    }, 40);
  };
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="stripe-setup-guide-btn">
          <BookOpen className="h-4 w-4 mr-1.5" /> Setup Guide
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto print-guide-content">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="text-2xl">Stripe — Step-by-step Setup Guide</DialogTitle>
            <Button variant="outline" size="sm" onClick={doPrint} data-testid="stripe-guide-print-btn" className="no-print shrink-0">
              <Printer className="h-4 w-4 mr-1.5" /> Print / Save as PDF
            </Button>
          </div>
        </DialogHeader>

        <div className="prose prose-sm max-w-none space-y-5 text-[13px] leading-relaxed">
          <section>
            <p className="text-neutral-700">
              This guide walks you through creating a Stripe account, generating the API keys the
              laundry app needs, and pasting them into the admin console. Set aside about
              <strong> 15 minutes</strong>. You will need: an email address, a phone number,
              your business ABN/EIN, and a bank account.
            </p>
          </section>

          <section>
            <h3 className="font-bold text-base mt-4">Part A — Create (or open) your Stripe account</h3>
            <ol className="list-decimal pl-6 space-y-1.5 mt-1">
              <li>Open <a className="text-brand-navy underline" href="https://dashboard.stripe.com/register" target="_blank" rel="noreferrer">https://dashboard.stripe.com/register <ExternalLink className="inline h-3 w-3"/></a> in a new tab. If you already have an account, sign in instead at <a className="text-brand-navy underline" href="https://dashboard.stripe.com/login" target="_blank" rel="noreferrer">dashboard.stripe.com/login</a>.</li>
              <li>Fill in your business email, full name, country and a strong password. Click <span className="font-semibold">Create account</span>.</li>
              <li>Confirm the verification link Stripe sends to your email inbox.</li>
              <li>Back in the dashboard, complete the <span className="font-semibold">Business profile</span> section on the left menu — business name, ABN, industry, website URL. Stripe blocks live payments until this is filled.</li>
              <li>Add your <span className="font-semibold">Bank account</span> under <span className="font-mono">Settings → Payouts</span>. This is where paid invoices will land.</li>
            </ol>
          </section>

          <section>
            <h3 className="font-bold text-base mt-4">Part B — Switch to LIVE mode and copy the API keys</h3>
            <ol className="list-decimal pl-6 space-y-1.5 mt-1">
              <li>Top-right of the Stripe dashboard, find the toggle labelled <span className="font-mono">View test data</span>. Click it OFF — the toggle should be dark/off so the URL reads <span className="font-mono">/dashboard</span> (not <span className="font-mono">/test/dashboard</span>).</li>
              <li>In the left sidebar, click <span className="font-mono">Developers</span>. If you don&apos;t see it, click the <span className="font-mono">⋯</span> menu near your profile picture and pick <span className="font-mono">Developers</span>.</li>
              <li>Click <span className="font-mono">API keys</span>. You&apos;ll see two rows: <strong>Publishable key</strong> and <strong>Secret key</strong>.</li>
              <li><strong>Publishable key</strong> (starts with <span className="font-mono">pk_live_</span>): click the value once to reveal, then click the copy icon.</li>
              <li><strong>Secret key</strong> (starts with <span className="font-mono">sk_live_</span>): click <span className="font-semibold">Reveal live key</span>, then click the copy icon.
                <br/><span className="text-red-700 font-semibold">⚠ Never share this or paste it into a chat/email. Treat it like a password.</span></li>
              <li>Leave the Stripe tab open — you&apos;ll need it again in Part C.</li>
            </ol>
          </section>

          <section>
            <h3 className="font-bold text-base mt-4">Part C — Create the Webhook endpoint (recommended)</h3>
            <p className="text-neutral-700 mt-1">
              Webhooks let Stripe notify our app when a payment succeeds or fails. Without them
              the app still works but invoices will show &quot;pending&quot; for longer.
            </p>
            <ol className="list-decimal pl-6 space-y-1.5 mt-1">
              <li>Still in the Stripe dashboard, go to <span className="font-mono">Developers → Webhooks</span>.</li>
              <li>Click <span className="font-semibold">Add endpoint</span> (top right).</li>
              <li>In the <span className="font-mono">Endpoint URL</span> field, paste exactly:
                <div className="my-2 p-2 rounded bg-neutral-100 border font-mono text-xs break-all">{API_BASE}/api/billing/webhook</div>
              </li>
              <li>Description: something like <span className="font-mono">Laundry app — payments</span>.</li>
              <li>Under <span className="font-semibold">Select events to listen to</span>, click <span className="font-semibold">Select events</span> and tick these:
                <ul className="list-disc pl-6 mt-1 text-neutral-700">
                  <li><span className="font-mono">invoice.paid</span></li>
                  <li><span className="font-mono">invoice.payment_failed</span></li>
                  <li><span className="font-mono">payment_intent.succeeded</span></li>
                  <li><span className="font-mono">payment_intent.payment_failed</span></li>
                  <li><span className="font-mono">setup_intent.succeeded</span></li>
                </ul>
              </li>
              <li>Click <span className="font-semibold">Add endpoint</span>.</li>
              <li>On the endpoint detail page, click <span className="font-semibold">Reveal</span> next to <span className="font-mono">Signing secret</span> and copy the value (starts with <span className="font-mono">whsec_</span>).</li>
            </ol>
          </section>

          <section>
            <h3 className="font-bold text-base mt-4">Part D — Paste into the laundry admin console</h3>
            <ol className="list-decimal pl-6 space-y-1.5 mt-1">
              <li>Return to this Settings page.</li>
              <li>Paste your <strong>Secret Key</strong> (<span className="font-mono">sk_live_…</span>) into the <span className="font-semibold">Secret Key</span> field.</li>
              <li>Paste your <strong>Publishable Key</strong> (<span className="font-mono">pk_live_…</span>) into the <span className="font-semibold">Publishable Key</span> field.</li>
              <li>Paste your <strong>Webhook Signing Secret</strong> (<span className="font-mono">whsec_…</span>) into the <span className="font-semibold">Webhook Secret</span> field. (Skip this if you didn&apos;t do Part C.)</li>
              <li>Click <span className="font-semibold">Verify &amp; Save</span>. The app will call Stripe with your key immediately — if the key is wrong you&apos;ll see a red error message and nothing is saved.</li>
              <li>Once saved, click <span className="font-semibold">Test Connection</span> — you should see a <span className="text-emerald-700 font-semibold">green &quot;Connected — key is valid&quot;</span> banner and a latency figure under 1000&nbsp;ms.</li>
            </ol>
          </section>

          <section>
            <h3 className="font-bold text-base mt-4">Part E — Sanity check</h3>
            <ol className="list-decimal pl-6 space-y-1.5 mt-1">
              <li>Go to a hotel with a real customer, click <span className="font-mono">Bind card</span> and enter a test card first: <span className="font-mono">4242 4242 4242 4242</span>, any future expiry, any CVC, any postcode.</li>
              <li>You&apos;ll see <em>test</em> cards fail because your keys are LIVE. That&apos;s correct — use a real card for the real customer.</li>
              <li>Create an invoice against the hotel, click <span className="font-semibold">Collect</span>, and confirm it lands in your Stripe dashboard under <span className="font-mono">Payments</span>.</li>
            </ol>
          </section>

          <section className="mt-4 p-3 rounded-md bg-amber-50 border border-amber-200">
            <p className="font-semibold text-amber-900">If something goes wrong</p>
            <ul className="list-disc pl-6 mt-1 space-y-1 text-amber-900 text-[12px]">
              <li><strong>&quot;Invalid API Key provided&quot;</strong> — you probably copied a test key (<span className="font-mono">sk_test_</span>) instead of live. Repeat Part B step 1.</li>
              <li><strong>&quot;Your account cannot currently make live charges&quot;</strong> — Stripe hasn&apos;t finished verifying your business. Complete <span className="font-mono">Business profile → Verify identity</span> in the dashboard.</li>
              <li><strong>&quot;Cloudflare 520&quot; or long timeout</strong> — the app couldn&apos;t reach Stripe. Try Test Connection again in 30 seconds; if it keeps failing, your server may be blocked outbound. Contact support.</li>
              <li><strong>Webhook signing errors in the log</strong> — the Signing secret you pasted doesn&apos;t match the endpoint. Repeat Part C step 7 and paste it again.</li>
            </ul>
          </section>

          <p className="text-[11px] text-muted-foreground pt-2 border-t">
            Generated by the Laundry admin console. Keys entered here are stored in the shared
            database and survive every code deploy — you do not need to redo this after we push
            updates. Rotate keys anytime from Stripe → Developers → API keys, then paste the new
            values here and Save.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
